import i18next from 'app/translations/i18n';
import { INTEGER, MAX, POSITIVE, REQUIRED } from 'app/utils/yupMessage';
import * as Yup from 'yup';
import { CONVERSION_MAX_PAGES } from './activityBatchConversion.constants';
import { getConversionPageSpan, getSourceUrlFileName } from './activityBatchConversion.form';

// Mensajes lazy: se resuelven al validar, con i18n ya inicializado.
const pdfRequiredError = () => i18next.t('activityBatches:digitalize_pdf_required');
const pdfInvalidError = () => i18next.t('activityBatches:pdf_invalid_content');
const pageRangeError = () => i18next.t('activityBatches:pdf_page_range_error');
const pageSpanError = () => i18next.t('activityBatches:digitalize_page_span_error', { max: CONVERSION_MAX_PAGES });

const toOptionalNumber = (value, original) => (original === '' || original === null || original === undefined ? undefined : value);

const isProvided = (value) => value !== undefined && value !== null && value !== '';

// El banco informa del total de páginas cuando lo conoce; entonces el rango no puede salirse del
// documento. Los esquemas de yup son inmutables, así que la misma constante sirve para los dos
// extremos del rango y `page_to` encadena sus tests sobre una copia.
const pageSchema = Yup.number()
  .transform(toOptionalNumber)
  .typeError(INTEGER)
  .integer(INTEGER)
  .positive(POSITIVE)
  .required(REQUIRED)
  // Sin `message`: el total de páginas solo se conoce al validar, así que el mensaje se compone
  // aquí con `count`, el nombre que i18next reserva para el plural de la clave.
  .test({
    name: 'page-in-document',
    test: function (value) {
      const count = Number(this.parent.page_count) || 0;
      if (!isProvided(value) || count <= 0 || value <= count) return true;

      throw this.createError({ message: i18next.t('activityBatches:digitalize_page_out_of_document_error', { count }) });
    },
  });

export const ConversionModelYup = Yup.object().shape({
  name: Yup.string().trim().max(250, MAX).required(REQUIRED),
  pdf_file_name: Yup.string(),
  source_ref_guid: Yup.string().required(pdfRequiredError),
  // Un contenido sin fichero (o con URL sin nombre de fichero) lo rechazaría v3: se corta aquí.
  source_url: Yup.string().when('source_ref_guid', (sourceRefGuid, schema) =>
    sourceRefGuid ? schema.required(pdfInvalidError).test('has-file-name', pdfInvalidError, (value) => Boolean(getSourceUrlFileName(value))) : schema
  ),
  page_count: Yup.number(),
  has_metadata: Yup.boolean(),
  is_pedagogy_complete: Yup.boolean(),
  page_from: pageSchema,
  page_to: pageSchema
    .test('page-to-gte-from', pageRangeError, function (value) {
      const from = this.parent.page_from;
      if (!isProvided(value) || !isProvided(from)) return true;

      return value >= Number(from);
    })
    .test('page-span-max', pageSpanError, function (value) {
      const from = this.parent.page_from;
      if (!isProvided(value) || !isProvided(from) || value < Number(from)) return true;

      return getConversionPageSpan(from, value) <= CONVERSION_MAX_PAGES;
    }),
  // El País solo filtra los catálogos por país; v3 lo prohíbe en el DTO, así que no es obligatorio.
  country: Yup.object().nullable(),
  educationLevel: Yup.object().nullable().required(REQUIRED),
  educationYear: Yup.object().nullable().required(REQUIRED),
  discipline: Yup.object().nullable().required(REQUIRED),
});
